import React from "react";

import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { IconButton, Tooltip } from "@material-ui/core";
import { isEqual } from "lodash";
import { useSelector } from "react-redux";

import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { ExclamationIcon } from "components/Icons";

import { ConsignmentModalSection } from "constants/navigation";
import { colors } from "constants/theme";

import { formatAddress } from "lib/address";
import { getGSTStatusText } from "lib/businesses";
import { openEditConsignmentModal } from "lib/navigation";

import {
  getBusinessById,
  getConsignmentById,
  getCurrentSale,
  getIsConsigningAgent,
  getNameByDeploymentId,
} from "selectors";
import { getBusinessRegistrationById } from "selectors/businessRegistrations";

export interface DeploymentBusinessNameProps {
  businessId: string;
}

export interface BusinessGSTStatusProps {
  businessId: string;
}

export interface DeploymentBusinessAddressProps {
  businessId: string;
}

export interface InvoiceNumberProps {
  businessId: string;
  consignment: Consignment & { livestocksale_id?: number };
}

export interface DeploymentBusinessLocalityProps {
  businessId: string;
}

export interface ConsignmentLocalityProps {
  consignmentId: string;
}

export interface ExtendedConsignment extends Consignment {
  livestocksale_id?: number;
  pickupAddress?: Address;
  useVendorAddressAsPickupAddress?: boolean;
}

// Components are defined separately before wrapping with React.memo to work around
// eslint-plugin-react bug: https://github.com/jsx-eslint/eslint-plugin-react/issues/2760
// The react/prop-types rule doesn't recognize TypeScript types when components are
// defined inline within React.memo(), causing false positives.

const DeploymentBusinessNameComponent = ({
  businessId,
}: DeploymentBusinessNameProps) => {
  const businessName =
    useSelector(state => getBusinessById(businessId)(state)?.name) || "";

  return <b data-tour="cardBusinessName">{businessName}</b>;
};

export const DeploymentBusinessName = React.memo(
  DeploymentBusinessNameComponent,
);

const BusinessGSTStatusComponent = ({ businessId }: BusinessGSTStatusProps) => {
  const business = useSelector(getBusinessById(businessId)) || ({} as Business);
  const registration = useSelector(
    getBusinessRegistrationById(business.registrationId),
  );
  const status = getGSTStatusText(
    registration?.isABNActive,
    registration?.isGSTRegistered,
    registration?.abrLastChecked,
    business.shouldChargeGSTOverride,
  );

  return <div>{`GST Status: ${status}`}</div>;
};

export const BusinessGSTStatus = React.memo(BusinessGSTStatusComponent);

const DeploymentBusinessAddressComponent = ({
  businessId,
}: DeploymentBusinessAddressProps) => {
  const formattedAddress = useSelector(state => {
    const address = getBusinessById(businessId)(state)?.address;
    return address ? formatAddress(address) : "Unknown";
  });

  return <div>{formattedAddress}</div>;
};

export const DeploymentBusinessAddress = React.memo(
  DeploymentBusinessAddressComponent,
);

const InvoiceNumberComponent = ({
  businessId,
  consignment,
}: InvoiceNumberProps) => {
  const business = useSelector(getBusinessById(businessId));
  return consignment && business ? (
    <div>{`Invoice: ${consignment.livestocksale_id}-${business.pk}`}</div>
  ) : null;
};

export const InvoiceNumber = React.memo(InvoiceNumberComponent);

const DeploymentBusinessLocalityComponent = ({
  businessId,
}: DeploymentBusinessLocalityProps) => {
  const locality =
    useSelector(getBusinessById(businessId))?.address?.locality || "";

  return <div>{locality}</div>;
};

export const DeploymentBusinessLocality = React.memo(
  DeploymentBusinessLocalityComponent,
);

const ConsignmentLocalityComponent = ({
  consignmentId,
}: ConsignmentLocalityProps) => {
  const consignment =
    useSelector(getConsignmentById(consignmentId)) ||
    ({} as ExtendedConsignment);
  const sale = useSelector(getCurrentSale);
  const deploymentId = sale?.deployment_sales[0]?.deployment_id || null;
  const deploymentName = useSelector(getNameByDeploymentId(deploymentId));
  const isConsigning = useSelector(getIsConsigningAgent);
  const editConsignment = () =>
    openEditConsignmentModal(
      consignmentId,
      ConsignmentModalSection.PICKUP_DETAILS,
      true,
    );

  const vendor =
    useSelector(getBusinessById(consignment.vendor_id || "")) ||
    ({} as Business);

  const matchingAddress = isEqual(consignment.pickupAddress, vendor.address);
  if (
    isConsigning &&
    consignment.useVendorAddressAsPickupAddress &&
    deploymentName
  ) {
    return (
      <Tooltip title={`Address manged by ${deploymentName}`}>
        <IconButton onClick={editConsignment}>
          <FaIcon icon={faQuestionCircle} color={colors.primary} />{" "}
        </IconButton>
      </Tooltip>
    );
  }
  if (
    isConsigning &&
    consignment.useVendorAddressAsPickupAddress &&
    !matchingAddress
  ) {
    return (
      <Tooltip title={`Address differs from ${deploymentName}'s`}>
        <IconButton onClick={editConsignment}>
          <ExclamationIcon color="warning" />{" "}
        </IconButton>
      </Tooltip>
    );
  }
  if (!consignment.useVendorAddressAsPickupAddress) {
    if (consignment.pickupAddress && consignment.pickupAddress.locality) {
      return <div>{consignment.pickupAddress.locality}</div>;
    }
    return <div />;
  }
  return (
    <DeploymentBusinessLocality businessId={consignment.vendor_id || ""} />
  );
};

export const ConsignmentLocality = React.memo(ConsignmentLocalityComponent);
